import { ImpersonationAlertBanner } from '@/auth/impersonation-alert-banner';
import { ImpersonationSwitcher } from '@/components/impersonation-switcher';
import { shouldRenderItem } from '@/components/should-render-item';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/tailwind-utils';
import { ExternalNavLink, NavLink, NavLinkDefinition } from '@/routes/_layout/-components/nav-link';
import { UserAvatarMenu } from '@/routes/_layout/-components/user-avatar-menu';
import { NAV } from '@airreading/automation-test/test-ids';
import { type ApiTargetUser, type LoggedInUser } from '@airreading/common/data';
import { isFullAirAdmin } from '@airreading/common';
import { Link } from '@tanstack/react-router';
import { ArrowLeftToLine, ArrowRightFromLine, PersonStanding } from 'lucide-react';
import { ReactNode } from 'react';

interface SidebarContentProps {
  matchedNavLinks: NavLinkDefinition[];
  loggedInUser: LoggedInUser;
  actingUser: ApiTargetUser | undefined;
  userIsStudent: boolean;
  isAdmin: boolean;
  /** School-admin portal. Shown when acting as a school admin. */
  isSchoolAdmin?: boolean;
  /**
   * Optional slot rendered next to the logo. Used by the mobile sheet to render
   * the close (ArrowLeft) button.
   */
  headerSlot?: ReactNode;
  /**
   * Called whenever a nav item is activated. Used by the mobile sheet to close
   * the drawer on selection.
   */
  onNavigate?: () => void;
  /** Icon-only mode for Admin / School Admin / Teacher desktop sidebars. */
  collapsed?: boolean;
  /** When set, shows the collapse/expand control at the top of the sidebar. */
  onToggleCollapsed?: () => void;
}

export const SidebarContent = ({
  matchedNavLinks,
  loggedInUser,
  actingUser,
  userIsStudent,
  isAdmin,
  isSchoolAdmin = false,
  headerSlot,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
}: SidebarContentProps) => {
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col justify-between">
      <div className="container min-w-0 px-0 py-2 grid overflow-auto">
        <div
          className={cn('container p-2 flex items-center mb-4 gap-1', {
            'justify-between': !collapsed,
            'flex-col justify-center': collapsed,
          })}
        >
          {!collapsed && (
            <div className="flex items-center justify-start gap-1 min-w-0">
              <Link to="/" className="flex items-center justify-center" data-testid={NAV.logo} onClick={onNavigate}>
                {userIsStudent ? (
                  <img src="/white-logo.svg" alt="Air Reading Logo" className="h-10" />
                ) : (
                  <img src="/logo.svg" alt="Air Reading Logo" className="h-10" />
                )}
              </Link>
              {isAdmin && (
                <div className="text-xs text-gray-400 h-[75%] flex items-center border-l border-border pl-2 shrink-0">
                  Admin portal
                </div>
              )}
              {isSchoolAdmin && !isAdmin && (
                <div className="text-xs text-gray-400 h-[75%] flex items-center border-l border-border pl-2 shrink-0">
                  School portal
                </div>
              )}
            </div>
          )}
          <div className={cn('flex items-center gap-1 shrink-0', { 'w-full justify-center': collapsed })}>
            {onToggleCollapsed && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!collapsed}
                data-testid={NAV.sidebarCollapseToggle}
                onClick={onToggleCollapsed}
                className="h-8 w-8 shrink-0 rounded-md text-muted-foreground"
              >
                {collapsed ? <ArrowRightFromLine className="h-4 w-4" /> : <ArrowLeftToLine className="h-4 w-4" />}
              </Button>
            )}
            {headerSlot}
          </div>
        </div>
        {matchedNavLinks.map((definition, index) => {
          if ('isDivider' in definition) {
            const visibleItem = actingUser
              ? shouldRenderItem(
                  {
                    ...actingUser,
                    fullName: actingUser.fullNameOrEmail,
                  },
                  definition.shouldRender,
                )
              : true;
            if (!visibleItem) return null;

            return <hr key={`divider-${index}`} className={cn('my-2 border-t border-border', { 'mx-2': collapsed })} />;
          }

          if ('externalURL' in definition) {
            return (
              <ExternalNavLink
                key={definition.label}
                linkDefinition={definition}
                onNavigate={onNavigate}
                collapsed={collapsed}
              />
            );
          }

          return (
            <NavLink
              key={`${definition.label}-${definition.linkProps.to}`}
              linkDefinition={definition}
              onNavigate={onNavigate}
              collapsed={collapsed}
            />
          );
        })}
      </div>
      <div className="min-w-0 w-full">
        {!collapsed && <ImpersonationAlertBanner />}
        <div
          className={cn('container min-w-0 p-2 flex items-center gap-x-2 justify-between border-t border-border', {
            'flex-col gap-y-2': collapsed,
          })}
        >
          <UserAvatarMenu compact={collapsed} />
          {!collapsed && isFullAirAdmin(loggedInUser) && (
            <ImpersonationSwitcher
              trigger={
                <Button
                  icon={PersonStanding}
                  variant={'ghost'}
                  aria-label="Impersonate as user"
                  className={cn('h-[4rem] w-[4rem] rounded-full', {
                    '[&>*]:stroke-white hover:bg-[#d23841]': userIsStudent,
                  })}
                />
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};
